import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaChevronLeft, 
  FaChevronRight,
  FaTh,
  FaBars,
  FaTimes
} from "react-icons/fa";

const NavigationSidebar = ({ tabs, activeTab, setActiveTab, onSidebarToggle }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we're in mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
      // Auto-collapse on mobile
      if (window.innerWidth < 1024) {
        setIsExpanded(false);
      }
    };
    
    // Initial check
    checkMobileView();
    
    // Add resize listener
    window.addEventListener('resize', checkMobileView);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Notify parent component when sidebar expansion changes
  useEffect(() => {
    if (onSidebarToggle) {
      onSidebarToggle(isExpanded);
    }
  }, [isExpanded, onSidebarToggle]);

  // Toggle sidebar expansion
  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Tab variants for animations
  const tabVariants = {
    active: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      transition: { duration: 0.2 }
    },
    inactive: {
      backgroundColor: "rgba(255, 255, 255, 0)",
      color: "#1f2937",
      transition: { duration: 0.2 }
    },
    hover: {
      backgroundColor: "#dbeafe",
      transition: { duration: 0.01 }
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };
    // Mobile hamburger menu button
  if (isMobileView && !isMobileMenuOpen) {
    return (
      <div className="fixed left-0 top-1/2 transform -translate-y-1/2 z-50">
        <button
          onClick={toggleMobileMenu}
          className="bg-blue-600 text-white p-3 rounded-r-lg shadow-lg"
          aria-label="Open navigation menu"
        >
          <FaBars className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // Render vertical sidebar
  return (    <div className={`${isMobileView ? 'fixed inset-0 z-50' : 'mb-6 md:mb-0 w-full lg:w-full'}`}>
      {/* Mobile overlay */}
      {isMobileView && (
        <div 
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={toggleMobileMenu}
        />
      )}
        <motion.div
        className={`relative bg-white shadow-md border border-gray-200 overflow-hidden flex flex-col 
          ${isMobileView ? 'h-full max-w-xs rounded-r-lg' : 'min-h-[70px] md:min-h-[500px] lg:h-[calc(100vh-240px)] lg:sticky lg:top-6 rounded-lg'}`}
        initial={isMobileView ? { x: "-100%" } : false}
        animate={isMobileView ? { x: 0 } : { width: isExpanded ? "240px" : "64px" }}
        style={!isMobileView ? { width: isExpanded ? "240px" : "64px" } : {}}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >{/* Header with brand and toggle buttons */}
        <div className="flex items-center justify-between border-b border-gray-200 py-3 px-4">
          <div className="flex items-center">
            {isMobileView ? (
              <div className="bg-blue-600 text-white p-2 rounded-md">
                <FaTh />
              </div>
            ) : (
              <button
                onClick={toggleExpansion}
                className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors"
                title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
              >
                {isExpanded ? <FaChevronLeft /> : <FaChevronRight />}
              </button>
            )}
            <AnimatePresence>
              {(isExpanded || isMobileView) && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="ml-3 font-semibold text-gray-800 whitespace-nowrap overflow-hidden truncate"
                >
                  Admin Dashboard
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          
          {/* Close button for mobile only */}
          {isMobileView && (
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600"
              aria-label="Close menu"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          )}
        </div>{/* Tab navigation */}
        <div className="flex flex-col p-2 space-y-1 flex-grow overflow-y-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            
            return (
              <motion.button
                key={tab.id}
                className={`
                  flex items-center rounded-md text-sm font-medium
                  ${(isExpanded || isMobileView) ? "px-4 justify-start" : "justify-center px-3"} py-3
                  ${isActive ? "text-white" : "text-gray-700"}
                  outline-none focus:ring-2 focus:ring-blue-300
                  transition-colors duration-200
                `}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (isMobileView) {
                    setIsMobileMenuOpen(false);
                  }
                }}
                initial="inactive"
                animate={isActive ? "active" : "inactive"}
                whileHover={!isActive ? "hover" : ""}
                whileTap="tap"
                variants={tabVariants}
                aria-label={tab.name}
              >
                <tab.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : "text-gray-500"}`} />
                <AnimatePresence>
                  {(isExpanded || isMobileView) && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="ml-3 whitespace-nowrap overflow-hidden truncate"
                    >
                      {tab.name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default NavigationSidebar;
